import { useState } from "react";
import toast from "react-hot-toast";
import { X } from "lucide-react";
import { useCreateEmployee, CONTRACT_TYPE_LABEL, ContractType } from "@/api/employees";
import { useClients, displayName } from "@/api/clients";
import { getApiErrorMessage } from "@/api/client";

const CONTRACT_TYPE_OPTIONS = Object.keys(CONTRACT_TYPE_LABEL) as ContractType[];

export default function CreateEmployeeModal({
  clientId: fixedClientId,
  onClose,
}: {
  clientId?: string;
  onClose: () => void;
}) {
  const createEmployee = useCreateEmployee();
  const [fullName, setFullName] = useState("");
  const [documentType, setDocumentType] = useState("CC");
  const [documentNumber, setDocumentNumber] = useState("");
  const [position, setPosition] = useState("");
  const [contractType, setContractType] = useState<ContractType>("TERMINO_INDEFINIDO");
  const [baseSalary, setBaseSalary] = useState("");
  const [startDate, setStartDate] = useState("");
  const [eps, setEps] = useState("");
  const [pensionFund, setPensionFund] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState<{ id: string; name: string } | null>(null);

  const { data: clientResults } = useClients({ search: clientSearch, pageSize: 6 });
  const effectiveClientId = fixedClientId ?? selectedClient?.id;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createEmployee.mutateAsync({
        clientId: effectiveClientId || undefined,
        fullName,
        documentType,
        documentNumber,
        position: position || undefined,
        contractType,
        baseSalary,
        startDate,
        eps: eps || undefined,
        pensionFund: pensionFund || undefined,
      });
      toast.success("Empleado creado correctamente.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Nuevo empleado</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          {!fixedClientId && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Cliente (opcional — vacío para nómina interna)</label>
              {selectedClient ? (
                <div className="mt-1 flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm">
                  <span>{selectedClient.name}</span>
                  <button type="button" className="text-xs text-gray-400 hover:text-red-600" onClick={() => setSelectedClient(null)}>
                    Quitar
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    className="input-field mt-1"
                    placeholder="Buscar cliente..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                  />
                  {clientSearch && clientResults?.data && clientResults.data.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                      {clientResults.data.map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                          onClick={() => {
                            setSelectedClient({ id: c.id, name: displayName(c) });
                            setClientSearch("");
                          }}
                        >
                          {displayName(c)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">Nombre completo</label>
            <input className="input-field mt-1" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Tipo doc.</label>
              <input className="input-field mt-1" value={documentType} onChange={(e) => setDocumentType(e.target.value)} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">N.° documento</label>
              <input className="input-field mt-1" value={documentNumber} onChange={(e) => setDocumentNumber(e.target.value)} required />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Cargo</label>
            <input className="input-field mt-1" value={position} onChange={(e) => setPosition(e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Tipo de contrato</label>
              <select className="input-field mt-1" value={contractType} onChange={(e) => setContractType(e.target.value as ContractType)}>
                {CONTRACT_TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {CONTRACT_TYPE_LABEL[t]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Salario base (COP)</label>
              <input type="number" min="0" className="input-field mt-1" value={baseSalary} onChange={(e) => setBaseSalary(e.target.value)} required />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Fecha de ingreso</label>
            <input type="date" className="input-field mt-1" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">EPS</label>
              <input className="input-field mt-1" value={eps} onChange={(e) => setEps(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Fondo de pensión</label>
              <input className="input-field mt-1" value={pensionFund} onChange={(e) => setPensionFund(e.target.value)} />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={createEmployee.isPending}>
              {createEmployee.isPending ? "Creando..." : "Crear empleado"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
